import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Input, Textarea, Button, Spinner } from '@cloudscape-design/components';
import { StageInfoWithIdAndDetails, StageUpdate } from '../../../common/types';
import { updateStage } from '../../../common/api/stage';
import { PuzzleView } from './puzzle';

export type StageViewProps = {
  stage: StageInfoWithIdAndDetails;
};

export function StageView(props: StageViewProps) {
  // Initialize local state from the stage prop.
  const [stage, setStage] = useState<StageInfoWithIdAndDetails>(props.stage);
  const [editedName, setEditedName] = useState(stage.name);
  const [editedMessage, setEditedMessage] = useState(stage.message);

  // Mutation for updating the stage details.
  const updateMutation = useMutation({
    mutationFn: async ({ stageUuid, update }: { stageUuid: string; update: StageUpdate }) =>
      updateStage(stageUuid, update),
    onSuccess: () => {
      // Update local state after a successful mutation.
      setStage((prev) => ({
        ...prev,
        name: editedName,
        message: editedMessage,
      }));
    },
  });

  const handleSave = useCallback(() => {
    updateMutation.mutate({
      stageUuid: stage.stageUuid,
      update: {
        name: editedName.trim(),
        message: editedMessage.trim(),
      },
    });
  }, [editedName, editedMessage, stage.stageUuid, updateMutation]);

  const loading = updateMutation.isPending;
  const changed = editedName !== stage.name || editedMessage !== stage.message;

  return (
    <div className="bg-[#1e2a38] p-4 rounded-lg shadow-md">
      {/* Layout: Two-column grid with image on the left and details on the right */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
        {/* Left Column: Stage Image and Caption */}
        <div className="flex justify-center items-start">
          <div className="relative">
            <img
              src={stage.image}
              alt={stage.caption}
              className="w-full max-w-[512px] rounded-lg object-cover"
            />
            {stage.caption && (
              <div className="absolute bottom-0 left-0 bg-black bg-opacity-50 text-white text-sm px-2 py-1 rounded-tr-lg">
                {stage.caption}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Editable Details */}
        <div className="flex flex-col justify-center space-y-4">
          <div>
            <div className="flex items-center w-full justify-center gap-4">
              <div className="flex items-center justify-center">
                <h2 className="text-xl font-medium text-gray-200">Stage</h2>
              </div>

              <div className="rounded-full bg-blue-500 flex items-center justify-center text-white font-bold w-12 h-12">
                <span className="text-lg">{stage.number}</span>
              </div>
            </div>
            <label className="block text-lg font-medium text-gray-200">Stage Name</label>
            <Input
              name="stageName"
              disabled={loading}
              placeholder="Stage name..."
              value={editedName}
              onChange={(e) => setEditedName(e.detail.value)}
            />
          </div>
          <div>
            <label className="block text-lg font-medium text-gray-200">Stage Message</label>
            <Textarea
              name="stageMessage"
              disabled={loading}
              placeholder="Stage message..."
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.detail.value)}
              rows={8}
            />
          </div>
          <div>
            <button
              className={`text-black font-semibold text-sm py-2 px-4 rounded ${
                loading || !changed ? 'bg-gray-400' : 'bg-white hover:bg-gray-200'
              }`}
              disabled={loading || !changed}
              onClick={handleSave}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <Spinner />
                  <span className="ml-2">Saving...</span>
                </div>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Puzzle List */}
      <div>
        <p className="text-gray-400 text-sm mb-1">Puzzles:</p>
        <div className="flex flex-col gap-4">
          {stage.puzzles.map((puzzle) => (
            <PuzzleView key={puzzle.puzzleUuid} puzzle={puzzle} />
          ))}
        </div>
      </div>
    </div>
  );
}
